/**
 * A page a reader created (stage 4) — `/page/:id`.
 *
 * Exactly `Structure.tsx`'s shape: ask `sectionsForPage` what's here, render each through
 * the shared registry. There is no page-specific content to special-case the way
 * `Briefing.tsx` has to for `tasks` — a created page starts empty and only ever holds
 * whatever a reader moved onto it, all of which fits the generic `{refreshKey, onChange}`
 * contract by construction (`sectionRegistry.tsx` only offers portable ids).
 */

import { useCallback, useState } from "react";
import { listPages, sectionsForPage, type SectionId } from "../layout/model";
import { renderSection } from "../layout/sectionRegistry";
import { LayoutGrid, type Section } from "../layout/LayoutGrid";
import { useQueueDelivered } from "../useQueueDelivered";

export function GenericPage({ id }: { id: string }) {
  const [revision, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((n) => n + 1), []);
  // A queued change landing is a change to this page's data like any other (D160). Declared
  // before the early return below, since a hook cannot be called conditionally.
  useQueueDelivered(bump);

  const exists = listPages().some((p) => p.id === id);
  if (!exists) {
    return (
      <section className="panel">
        <div className="empty">
          <strong>This page no longer exists.</strong>
          <p className="small muted" style={{ margin: "0.4rem 0 0" }}>
            It was deleted on this device. Anything that was on it moved back to wherever it
            starts by default.
          </p>
        </div>
      </section>
    );
  }

  const sections: Section[] = sectionsForPage(id)
    .map((sectionId): Section | null => {
      const node = renderSection(sectionId as SectionId, { refreshKey: revision, onChange: bump });
      return node ? { id: sectionId, node } : null;
    })
    .filter((section): section is Section => section !== null);

  return <LayoutGrid page={id} sections={sections} onSectionsChanged={bump} />;
}
